// PlanosCliente.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { PlanosService } from '../estabelecimento/Service';
import type { Plano } from './planoTypes';
import PlanoAssinaturaForm from './plano-assinatura/PlanoAssinaturaForm';
import { useEstabsContext } from '../../../contexts/EstabsContext';

import PlanosClientePlanosList from './PlanosClientePlanosList';
import PlanosClienteAssinaturasList from './PlanosClienteAssinaturasList'; // ✅ import certo
import '../Planos.css';
import { ChevronLeft, Store } from 'lucide-react';

const service = new PlanosService();

type Step = 'lista' | 'form';
type Tab = 'planos' | 'assinaturas';

export default function PlanosCliente() {
    const { estabsUser } = useEstabsContext();

    const [selectedPlano, setSelectedPlano] = useState<Plano | null>(null);
    const [step, setStep] = useState<Step>('lista');

    const [selectedEstabId, setSelectedEstabId] = useState<string>('');
    const [activeTab, setActiveTab] = useState<Tab>('planos');

    // Apenas estabelecimentos onde o usuário é CLIENTE
    const clientEstabs = useMemo(
        () => (estabsUser || []).filter((e: any) => e.type === 'client'),
        [estabsUser],
    );

    const selectedEstab = useMemo(
        () => clientEstabs.find((e: any) => e._id === selectedEstabId),
        [clientEstabs, selectedEstabId],
    );

    // Define estab padrão (se tiver só um, já seleciona)
    useEffect(() => {
        if (!clientEstabs.length) {
            setSelectedEstabId('');
            return;
        }

        setSelectedEstabId((current) => {
            const stillExists = clientEstabs.some((e: any) => e._id === current);
            if (current && stillExists) return current;
            return clientEstabs[0]._id;
        });
    }, [clientEstabs]);

    // Ao clicar em "Contratar" no card
    const handleContratarPlano = (plano: Plano) => {
        setSelectedPlano(plano);
        setStep('form');
        window.scrollTo({ top: 0 });
    };

    // Botão "voltar" dentro da etapa de formulário
    const handleVoltarParaLista = () => {
        setStep('lista');
    };

    // Depois de contratar, mostra a assinatura recem-criada.
    const handleAssinaturaCriada = () => {
        setActiveTab('assinaturas');
        setStep('lista');
        window.scrollTo({ top: 0 });
    };

    if (step === 'form') {
        // TELA DO FORMULÁRIO APENAS
        return (
            <div className="pla-checkout px-2 pb-6">
                <button type="button" onClick={handleVoltarParaLista} className="sf-back-button -ml-1 mb-3">
                    <ChevronLeft aria-hidden />
                    Planos
                </button>

                <PlanoAssinaturaForm
                    plano={selectedPlano}
                    estabId={selectedEstabId}
                    estabName={clientEstabs.length > 1 ? selectedEstab?.name : undefined}
                    back={handleVoltarParaLista}
                    onSuccess={handleAssinaturaCriada}
                />
            </div>
        );
    }

    if (!clientEstabs.length) {
        return (
            <div className="sf-state">
                <Store aria-hidden />
                <h2>Nenhum estabelecimento vinculado</h2>
                <p>Os planos aparecem aqui quando você for cliente de um estabelecimento.</p>
            </div>
        );
    }

    // step === 'lista'
    return (
        <div className="pla-page px-2">
            <div className="pla-toolbar">
                <div className="sf-segmented" role="group" aria-label="Planos e assinaturas">
                    <button type="button" aria-pressed={activeTab === 'planos'} onClick={() => setActiveTab('planos')}>
                        Planos
                    </button>
                    <button
                        type="button"
                        aria-pressed={activeTab === 'assinaturas'}
                        onClick={() => setActiveTab('assinaturas')}
                    >
                        Minhas assinaturas
                    </button>
                </div>

                {clientEstabs.length > 1 && (
                    <select
                        className="sf-select-field"
                        value={selectedEstabId}
                        onChange={(e) => setSelectedEstabId(e.target.value)}
                        aria-label="Estabelecimento"
                    >
                        {clientEstabs.map((estab: any) => (
                            <option key={estab._id} value={estab._id}>
                                {estab.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {selectedEstabId && activeTab === 'planos' && (
                <PlanosClientePlanosList
                    estabId={selectedEstabId}
                    onContratar={handleContratarPlano}
                    service={service}
                />
            )}

            {selectedEstabId && activeTab === 'assinaturas' && (
                <PlanosClienteAssinaturasList estabId={selectedEstabId} />
            )}
        </div>
    );
}
